import { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "react-grid-layout";

import {
  WIDGET_GRID_COLS,
  WIDGET_GRID_MARGIN,
  WIDGET_GRID_ROW_HEIGHT,
} from "@/features/shared/components/widget-grid-layout/widget-grid-layout";
import {
  calcColumnWidth,
  pixelDeltaToUnits,
} from "@/features/admin/features/pages-management/utils/grid-resize-math";
import { applySeResizeStep } from "@/features/admin/features/pages-management/utils/apply-single-widget-resize";
import { isLayoutEqual } from "@/features/admin/features/pages-management/utils/is-layout-equal";

type ActiveResize = { i: string; w: number; h: number };

type ResizeSession = {
  pointerId: number;
  containerId: string;
  startClientX: number;
  startClientY: number;
  startW: number;
  startH: number;
  minW: number;
  minH: number;
  maxW: number;
  columnWidth: number;
  /** Layout as it was when the resize started; every step is derived from this. */
  snapshot: Layout[];
};

type Options = {
  /** Pixel width of the grid container, measured when a resize starts. */
  getContainerWidth: () => number;
  getLayout: () => Layout[];
  onLayoutChange: (layout: Layout[]) => void;
};

/**
 * Controlled bottom-right resize for a single widget.
 *
 * react-grid-layout ignores the `layouts` prop while its own resize is active
 * and compacts on every step. Driving the resize ourselves keeps the grid fully
 * controlled. Multi-widget width exchange belongs to gutter handles instead.
 */
export function useLayoutResize({ getContainerWidth, getLayout, onLayoutChange }: Options) {
  const [activeResize, setActiveResize] = useState<ActiveResize | null>(null);

  const sessionRef = useRef<ResizeSession | null>(null);
  const emittedRef = useRef<Layout[] | null>(null);

  const onLayoutChangeRef = useRef(onLayoutChange);
  onLayoutChangeRef.current = onLayoutChange;

  useEffect(() => {
    const [marginX, marginY] = WIDGET_GRID_MARGIN;

    const handlePointerMove = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;

      const deltaColumns = pixelDeltaToUnits(
        event.clientX - session.startClientX,
        session.columnWidth,
        marginX,
      );
      const deltaRows = pixelDeltaToUnits(
        event.clientY - session.startClientY,
        WIDGET_GRID_ROW_HEIGHT,
        marginY,
      );

      const {
        layout: next,
        w,
        h,
      } = applySeResizeStep(
        session.snapshot,
        session.containerId,
        session.startW,
        session.startH,
        deltaColumns,
        deltaRows,
        session.minW,
        session.minH,
        session.maxW,
      );

      setActiveResize((prev) =>
        prev && prev.w === w && prev.h === h ? prev : { i: session.containerId, w, h },
      );

      if (emittedRef.current && isLayoutEqual(next, emittedRef.current)) return;
      emittedRef.current = next;
      onLayoutChangeRef.current(next);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      sessionRef.current = null;
      emittedRef.current = null;
      setActiveResize(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, []);

  const startResize = useCallback(
    (containerId: string, event: React.PointerEvent<HTMLElement>) => {
      const layout = getLayout();
      const item = layout.find((candidate) => candidate.i === containerId);
      const containerWidth = getContainerWidth();
      if (!item || containerWidth <= 0) return;

      // Keep the browser from starting a text selection or a grid drag.
      event.preventDefault();
      event.stopPropagation();

      sessionRef.current = {
        pointerId: event.pointerId,
        containerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startW: item.w,
        startH: item.h,
        minW: item.minW ?? 1,
        minH: item.minH ?? 1,
        maxW: WIDGET_GRID_COLS - item.x,
        columnWidth: calcColumnWidth(
          containerWidth,
          WIDGET_GRID_COLS,
          WIDGET_GRID_MARGIN[0],
        ),
        snapshot: layout,
      };
      emittedRef.current = layout;
      setActiveResize({ i: item.i, w: item.w, h: item.h });
    },
    [getContainerWidth, getLayout],
  );

  return { activeResize, startResize, isResizing: activeResize !== null };
}
